"use client";

import { useServerErrorMessage } from "@/lib/i18n/use-server-error-message";
import { api } from "@v1/backend/convex/_generated/api";
import type { Id } from "@v1/backend/convex/_generated/dataModel";
import { useMutation, usePaginatedQuery } from "convex/react";
import { useTranslations } from "next-intl";
import { type ReactElement, type ReactNode, useMemo } from "react";
import { toast } from "sonner";
import { type MessengerBubbleItem, MessengerBubbles } from "./messenger-bubbles";
import { MessengerComposer } from "./messenger-composer";
import { useMessengerSurface } from "./messenger-context";
import { MessengerDoneBar, MessengerEventRow, MessengerLoadOlder } from "./messenger-thread-parts";
import { useMessengerStickToBottom } from "./use-messenger-scroll";

const PAGE_SIZE = 30;

/** Ticket messages (team replies, copied chat) and the reply box. */
export function MessengerTicketThread({
  taskId,
  done,
  handedAt,
}: {
  readonly taskId: Id<"tasks">;
  readonly done: boolean;
  /** When the chat became this ticket; shown as an event row at that point. */
  readonly handedAt?: number;
}): ReactElement {
  const t = useTranslations("messenger.thread");
  const errorMessage = useServerErrorMessage();
  const surface = useMessengerSurface();
  const send = useMutation(api.customerChat.messenger_writes.sendTicketMessage);
  const { results, status, loadMore } = usePaginatedQuery(
    api.customerChat.messenger_thread.listTicketMessages,
    { surface, task_id: taskId },
    { initialNumItems: PAGE_SIZE },
  );
  const items = useMemo(
    (): MessengerBubbleItem[] =>
      [...results].reverse().map((m) => ({
        id: m.id,
        author: m.kind,
        name: m.name,
        text: m.text,
        at: m.created_at,
        commentId: m.id,
        attachments: m.attachments,
      })),
    [results],
  );
  const scrollRef = useMessengerStickToBottom(`${items.length}:${items[items.length - 1]?.id ?? ""}`);

  const splitAt =
    handedAt === undefined ? items.length : items.findIndex((item) => item.at !== null && item.at > handedAt);
  const before = splitAt < 0 ? items : items.slice(0, splitAt);
  const after = splitAt < 0 ? [] : items.slice(splitAt);
  const event: ReactNode = handedAt === undefined ? null : <MessengerEventRow at={handedAt} />;

  return (
    <>
      <div ref={scrollRef} className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
        {status === "CanLoadMore" ? <MessengerLoadOlder onClick={() => loadMore(PAGE_SIZE)} /> : null}
        {status === "LoadingFirstPage" ? null : (
          <>
            <MessengerBubbles items={before} taskId={taskId} />
            {event}
            {after.length > 0 ? <MessengerBubbles items={after} taskId={taskId} /> : null}
          </>
        )}
      </div>
      {done ? (
        <MessengerDoneBar />
      ) : (
        <MessengerComposer
          onSend={async (text) => {
            try {
              await send({ surface, task_id: taskId, text });
              return true;
            } catch (error) {
              toast.error(errorMessage(error, t("fehler")));
              return false;
            }
          }}
        />
      )}
    </>
  );
}
